import { addressToBytes32 } from "@layerzerolabs/lz-v2-utilities";
import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLatest } from "react-use";
import { decodeErrorResult, encodeEventTopics, isHex, toHex, zeroAddress } from "viem";
import { useAccount, useChains } from "wagmi";

import { AVALANCHE, AnyChainId, SettlementChainId, SourceChainId, getChainName, isTestnetChain } from "config/chains";
import { getContract } from "config/contracts";
import { getChainIcon } from "config/icons";
import {
  CHAIN_ID_PREFERRED_DEPOSIT_TOKEN,
  MULTICHAIN_FUNDING_SLIPPAGE_BPS,
  MULTI_CHAIN_DEPOSIT_TRADE_TOKENS,
  RANDOM_ACCOUNT,
  StargateErrorsAbi,
  getDepositTokenSymbolsForNetwork,
  getMappedTokenId,
} from "config/multichain";
import {
  useGmxAccountDepositViewChain,
  useGmxAccountDepositViewTokenAddress,
  useGmxAccountDepositViewTokenInputValue,
  useGmxAccountModalOpen,
  useGmxAccountSelectedTransferGuid,
  useGmxAccountSelector,
  useGmxAccountSettlementChainId,
  useGmxAccountWalletReceiveViewBackTo,
  useGmxAccountWalletReceiveViewChain,
} from "context/GmxAccountContext/hooks";
import { selectGmxAccountDepositViewTokenInputAmount } from "context/GmxAccountContext/selectors";
import { useSubaccountContext } from "context/SubaccountContext/SubaccountContextProvider";
import { useSyntheticsEvents } from "context/SyntheticsEvents";
import { useMultichainApprovalsActiveListener } from "context/SyntheticsEvents/useMultichainEvents";
import { getMultichainTransferSendParams } from "domain/multichain/getSendParams";
import { isStringEqualInsensitive, matchLogRequest } from "domain/multichain/progress/LongCrossChainTask";
import { sendCrossChainDepositTxn } from "domain/multichain/sendCrossChainDepositTxn";
import { estimateSameChainDepositGas, sendSameChainDepositTxn } from "domain/multichain/sendSameChainDepositTxn";
import { SendParam } from "domain/multichain/types";
import { useGmxAccountFundingHistory } from "domain/multichain/useGmxAccountFundingHistory";
import { useMultichainDepositNetworkComposeGas } from "domain/multichain/useMultichainDepositNetworkComposeGas";
import { useMultichainQuoteFeeUsd } from "domain/multichain/useMultichainQuoteFeeUsd";
import { useNativeTokenBalance } from "domain/multichain/useNativeTokenBalance";
import { useQuoteOft } from "domain/multichain/useQuoteOft";
import { useQuoteOftLimits } from "domain/multichain/useQuoteOftLimits";
import { useQuoteSendNativeFeeWithGasLimit } from "domain/multichain/useQuoteSend";
import { useGasPrice } from "domain/synthetics/fees/useGasPrice";
import { getBalanceByBalanceType, useTokensDataRequest } from "domain/synthetics/tokens";
import { ValidationBannerErrorName, getDefaultInsufficientGasMessage } from "domain/synthetics/trade/utils/validation";
import { NativeTokenSupportedAddress } from "domain/tokens";
import { useMaxAvailableAmount } from "domain/tokens/useMaxAvailableAmount";
import { useTokenApproval } from "domain/tokens/useTokenApproval";
import { AddressablePixelEventName, sendAddressablePixelEvent } from "lib/addressablePixel";
import { useChainId } from "lib/chains";
import { useMultipleWalletExtensionsChainError } from "lib/chains/getMultipleWalletExtensionsChainError";
import { useLeadingDebounce } from "lib/debounce/useLeadingDebounde";
import { helperToast } from "lib/helperToast";
import { useLocalizedList } from "lib/i18n";
import {
  OrderMetricId,
  initMultichainDepositMetricData,
  sendOrderSimulatedMetric,
  sendOrderSubmittedMetric,
  sendOrderTxnSubmittedMetric,
  sendTxnErrorMetric,
  sendTxnSentMetric,
} from "lib/metrics";
import { USD_DECIMALS, adjustForDecimals, bigintToNumber, expandDecimals, formatUsd } from "lib/numbers";
import { EMPTY_ARRAY, EMPTY_OBJECT, getByKey } from "lib/objects";
import { TxnCallback, TxnEventName, WalletTxnCtx } from "lib/transactions";
import { getPageOutdatedError, useHasOutdatedUi } from "lib/useHasOutdatedUi";
import { useThrottledAsync } from "lib/useThrottledAsync";
import { useIsNonEoaAccountOnAnyChain } from "lib/wallets/useAccountType";
import { useIsGeminiWallet } from "lib/wallets/useIsGeminiWallet";
import { getPublicClientWithRpc } from "lib/wallets/walletConfig";
import { abis } from "sdk/abis";
import { convertTokenAddress, getToken } from "sdk/configs/tokens";
import { bigMath } from "sdk/utils/bigmath";
import { TokenBalanceType, TokenData, convertToTokenAmount, convertToUsd, getMidPrice } from "sdk/utils/tokens";
import { applySlippageToMinOut } from "sdk/utils/trade";

import { AlertInfoCard } from "components/AlertInfo/AlertInfoCard";
import { AmountWithUsdBalance } from "components/AmountWithUsd/AmountWithUsd";
import Button from "components/Button/Button";
import { DropdownSelector } from "components/DropdownSelector/DropdownSelector";
import { getTxnErrorToast } from "components/Errors/errorToasts";
import { ValidationBannerErrorContent } from "components/Errors/gasErrors";
import NumberInput from "components/NumberInput/NumberInput";
import { SyntheticsInfoRow } from "components/SyntheticsInfoRow";
import TokenIcon from "components/TokenIcon/TokenIcon";
import { ButtonTooltipWrapper } from "components/Tooltip/ButtonTooltipWrapper";
import { ValueTransition } from "components/ValueTransition/ValueTransition";

import ChevronRightIcon from "img/ic_chevron_right.svg?react";
import SpinnerIcon from "img/ic_spinner.svg?react";

import { calculateNetworkFeeDetails } from "./calculateNetworkFeeDetails";
import {
  useAvailableToTradeAssetMultichain,
  useGmxAccountDepositEligibility,
  useGmxAccountDepositNetworks,
  useMultichainTradeTokensRequest,
} from "./hooks";
import { wrapChainAction } from "./wrapChainAction";

const valueSkeleton = (
  <Skeleton
    baseColor="#B4BBFF1A"
    highlightColor="#B4BBFF1A"
    width={96}
    height={14}
    borderRadius={4}
    className="leading-[14px]"
    inline
  />
);

const useIsFirstDeposit = () => {
  const [enabled, setEnabled] = useState(true);
  const [isFirstDeposit, setIsFirstDeposit] = useState(false);
  const { fundingHistory, isLoading } = useGmxAccountFundingHistory({ enabled });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (fundingHistory === undefined || fundingHistory.length !== 0) {
      return;
    }

    setEnabled(false);
    const hasDeposit = fundingHistory.some((funding) => funding.operation === "deposit");
    if (!hasDeposit) {
      setIsFirstDeposit(true);
    }
  }, [fundingHistory, isLoading]);

  return isFirstDeposit;
};

export const DepositView = () => {
  const { chainId: settlementChainId, srcChainId } = useChainId();
  const { address: account, chainId: walletChainId } = useAccount();

  const [, setSettlementChainId] = useGmxAccountSettlementChainId();
  const [depositViewChain, setDepositViewChain] = useGmxAccountDepositViewChain();

  const [isVisibleOrView, setIsVisibleOrView] = useGmxAccountModalOpen();
  const [, setSelectedTransferGuid] = useGmxAccountSelectedTransferGuid();

  const [depositViewTokenAddress, setDepositViewTokenAddress] = useGmxAccountDepositViewTokenAddress();
  const [inputValue, setInputValue] = useGmxAccountDepositViewTokenInputValue();
  const { tokenChainDataArray: multichainTokens, isPriceDataLoading } = useMultichainTradeTokensRequest(
    settlementChainId,
    account
  );
  const { tokensData: settlementChainTokensData } = useTokensDataRequest(settlementChainId, depositViewChain);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldSendCrossChainDepositWhenLoaded, setShouldSendCrossChainDepositWhenLoaded] = useState(false);

  const [, setWalletReceiveViewChain] = useGmxAccountWalletReceiveViewChain();
  const [, setWalletReceiveViewBackTo] = useGmxAccountWalletReceiveViewBackTo();

  const depositNetworks = useGmxAccountDepositNetworks();
  const { hasAnyDepositFunds, hasDepositFundsOnChain, isEligibilityLoading } = useGmxAccountDepositEligibility();
  const fromNetworkButtonRef = useRef<HTMLButtonElement>(null);

  const { setMultichainSubmittedDeposit, setMultichainFundingPendingId } = useSyntheticsEvents();

  const selectedToken =
    depositViewTokenAddress !== undefined ? getToken(settlementChainId, depositViewTokenAddress) : undefined;

  const { tokensData } = useTokensDataRequest(settlementChainId, depositViewChain);
  const selectedTokenData = getByKey(tokensData, depositViewTokenAddress);

  const selectedTokenSourceChainTokenId =
    depositViewTokenAddress !== undefined && depositViewChain !== undefined
      ? getMappedTokenId(settlementChainId as SettlementChainId, depositViewTokenAddress, depositViewChain)
      : undefined;

  const unwrappedSelectedTokenAddress =
    depositViewTokenAddress !== undefined
      ? convertTokenAddress(settlementChainId, depositViewTokenAddress, "native")
      : undefined;

  const selectedTokenChainData = useMemo(() => {
    if (selectedToken === undefined) {
      return undefined;
    }

    if (depositViewChain === settlementChainId) {
      const settlementChainTokenData = getByKey(settlementChainTokensData, selectedToken.address);
      if (!settlementChainTokenData) {
        return undefined;
      }
      return {
        ...settlementChainTokenData,
        sourceChainId: depositViewChain,
        sourceChainDecimals: settlementChainTokenData?.decimals,
        sourceChainPrices: settlementChainTokenData?.prices,
        sourceChainBalance: settlementChainTokenData?.walletBalance,
      };
    }

    return multichainTokens.find(
      (token) => token.address === selectedToken.address && token.sourceChainId === depositViewChain
    );
  }, [selectedToken, depositViewChain, settlementChainId, multichainTokens, settlementChainTokensData]);

  const selectedTokenSourceChainBalance = selectedTokenChainData?.sourceChainBalance;
  const selectedTokenSourceChainDecimals = selectedTokenChainData?.sourceChainDecimals;

  const viemChains = useChains();
  const depositViewViemChain = useMemo(
    () =>
      depositViewChain !== undefined && viemChains.length > 0
        ? viemChains.find((chain) => chain.id === depositViewChain)
        : undefined,
    [viemChains, depositViewChain]
  );

  const nativeTokenSourceChainBalance = useNativeTokenBalance(depositViewChain, account);
  const selectedTokenBalanceForDeposit =
    depositViewChain !== settlementChainId && unwrappedSelectedTokenAddress === zeroAddress
      ? nativeTokenSourceChainBalance
      : selectedTokenSourceChainBalance;

  const realInputAmount = useGmxAccountSelector(selectGmxAccountDepositViewTokenInputAmount);

  /**
   * Debounced
   */
  const inputAmount = useLeadingDebounce(realInputAmount);
  const inputAmountUsd = selectedToken
    ? convertToUsd(inputAmount, selectedToken.decimals, selectedTokenChainData?.sourceChainPrices?.maxPrice)
    : undefined;
  const latestInputAmountUsd = useLatest(inputAmountUsd);

  const amountLD =
    inputAmount !== undefined && selectedTokenSourceChainDecimals !== undefined && selectedToken?.decimals !== undefined
      ? (inputAmount * 10n ** BigInt(selectedTokenSourceChainDecimals)) / 10n ** BigInt(selectedToken?.decimals)
      : undefined;

  const { gmxAccountUsd, isLoading: isGmxAccountUsdLoading } = useAvailableToTradeAssetMultichain();

  const { nextGmxAccountBalanceUsd } = useMemo((): {
    nextGmxAccountBalanceUsd?: bigint;
    nextTokenGmxAccountBalance?: bigint;
  } => {
    if (inputAmount === undefined || inputAmountUsd === undefined) {
      return EMPTY_OBJECT;
    }

    const nextGmxAccountBalanceUsd = (gmxAccountUsd ?? 0n) + inputAmountUsd;
    const nextTokenGmxAccountBalance = (selectedTokenData?.gmxAccountBalance ?? 0n) + inputAmount;

    return {
      nextGmxAccountBalanceUsd,
      nextTokenGmxAccountBalance,
    };
  }, [gmxAccountUsd, inputAmount, inputAmountUsd, selectedTokenData?.gmxAccountBalance]);

  const spenderAddress =
    // Only when DEBUG_MULTICHAIN_SAME_CHAIN_DEPOSIT
    (depositViewChain as AnyChainId) === settlementChainId
      ? getContract(settlementChainId, "SyntheticsRouter")
      : selectedTokenSourceChainTokenId?.stargate;

  useMultichainApprovalsActiveListener(depositViewChain, "multichain-deposit-view");

  const sourceChainTokenToApproveAddress: string | undefined = useMemo(() => {
    if (depositViewChain === settlementChainId) {
      return depositViewTokenAddress;
    }
    return selectedTokenSourceChainTokenId?.address;
  }, [depositViewChain, depositViewTokenAddress, selectedTokenSourceChainTokenId?.address, settlementChainId]);

  const approvalTokens = useMemo(
    () =>
      sourceChainTokenToApproveAddress && amountLD !== undefined
        ? [{ tokenAddress: sourceChainTokenToApproveAddress, amount: amountLD }]
        : EMPTY_ARRAY,
    [sourceChainTokenToApproveAddress, amountLD]
  );

  const {
    needsApproval: needTokenApprove,
    isApproving,
    handleApprove,
  } = useTokenApproval({
    chainId: depositViewChain,
    spenderAddress,
    tokens: approvalTokens,
    skip:
      depositViewChain === undefined ||
      !sourceChainTokenToApproveAddress ||
      sourceChainTokenToApproveAddress === zeroAddress,
  });

  const handleApproveClick = useCallback(() => {
    if (!depositViewTokenAddress || !spenderAddress || !depositViewChain) {
      helperToast.error(t`Approval failed`);
      return;
    }

    const isNative = depositViewTokenAddress === zeroAddress;

    if (isNative) {
      helperToast.error(t`Native token cannot be approved`);
      return;
    }

    if (!sourceChainTokenToApproveAddress) {
      helperToast.error(t`Approval failed`);
      return;
    }

    handleApprove();
  }, [depositViewChain, depositViewTokenAddress, handleApprove, sourceChainTokenToApproveAddress, spenderAddress]);

  const isInputEmpty = inputAmount === undefined || inputAmount <= 0n || amountLD === undefined || amountLD <= 0n;

  const { composeGas, isLoading: isComposeGasLoading } = useMultichainDepositNetworkComposeGas({
    tokenAddress: depositViewTokenAddress,
  });

  const sendParamsWithoutSlippage: SendParam | undefined = useMemo(() => {
    if (
      !account ||
      amountLD === undefined ||
      amountLD <= 0n ||
      depositViewChain === undefined ||
      composeGas === undefined
    ) {
      return;
    }

    return getMultichainTransferSendParams({
      account,
      amountLD,
      srcChainId: depositViewChain,
      composeGas,
      dstChainId: settlementChainId,
      isToGmx: true,
    });
  }, [account, amountLD, depositViewChain, composeGas, settlementChainId]);

  const baseSendParams: SendParam | undefined = useMemo(() => {
    if (depositViewChain === undefined || composeGas === undefined) {
      return;
    }

    const decimals = selectedTokenSourceChainTokenId?.decimals;
    const prices = selectedTokenChainData?.sourceChainPrices;
    if (decimals === undefined || prices === undefined) {
      return;
    }

    const amountLD = convertToTokenAmount(expandDecimals(10, USD_DECIMALS), decimals, getMidPrice(prices))!;

    return getMultichainTransferSendParams({
      account: RANDOM_ACCOUNT.address,
      amountLD,
      srcChainId: depositViewChain,
      composeGas,
      dstChainId: settlementChainId,
      isToGmx: true,
    });
  }, [
    composeGas,
    depositViewChain,
    selectedTokenChainData?.sourceChainPrices,
    selectedTokenSourceChainTokenId?.decimals,
    settlementChainId,
  ]);

  const { data: quoteOft, isLoading: isQuoteOftLoading } = useQuoteOft({
    sendParams: sendParamsWithoutSlippage,
    fromStargateAddress: selectedTokenSourceChainTokenId?.stargate,
    fromChainId: depositViewChain,
    toChainId: settlementChainId,
  });

  const { isBelowLimit, lowerLimitFormatted, isAboveLimit, upperLimitFormatted } = useQuoteOftLimits({
    quoteOft,
    amountLD,
    isStable: selectedToken?.isStable,
    decimals: selectedTokenSourceChainTokenId?.decimals,
    symbol: selectedToken?.symbol,
    enabled: depositViewChain !== settlementChainId,
  });

  const sendParamsWithSlippage: SendParam | undefined = useMemo(() => {
    if (!quoteOft || !sendParamsWithoutSlippage) {
      return undefined;
    }

    const { receipt } = quoteOft;

    const minAmountLD = applySlippageToMinOut(MULTICHAIN_FUNDING_SLIPPAGE_BPS, receipt.amountReceivedLD as bigint);

    const newSendParams: SendParam = {
      ...sendParamsWithoutSlippage,
      minAmountLD,
    };

    return newSendParams;
  }, [sendParamsWithoutSlippage, quoteOft]);

  const { data: quoteSendData, isLoading: isQuoteSendNativeFeeLoading } = useQuoteSendNativeFeeWithGasLimit({
    sendParams: sendParamsWithSlippage,
    fromStargateAddress: selectedTokenSourceChainTokenId?.stargate,
    fromChainId: depositViewChain,
    toChainId: settlementChainId,
    fromTokenAddress: selectedTokenSourceChainTokenId?.address,
    composeGas,
  });

  const { data: baseQuoteSendData, isLoading: isBaseQuoteSendNativeFeeLoading } = useQuoteSendNativeFeeWithGasLimit({
    sendParams: baseSendParams,
    fromStargateAddress: selectedTokenSourceChainTokenId?.stargate,
    fromChainId: depositViewChain,
    toChainId: settlementChainId,
    fromTokenAddress: selectedTokenSourceChainTokenId?.address,
    composeGas,
  });

  const quoteSendNativeFee = quoteSendData?.nativeFee;
  const baseQuoteSendNativeFee = baseQuoteSendData?.nativeFee;

  const { networkFee, networkFeeUsd, protocolFeeAmount, protocolFeeUsd } = useMultichainQuoteFeeUsd({
    quoteSendNativeFee: quoteSendNativeFee ?? baseQuoteSendNativeFee,
    quoteOft,
    unwrappedTokenAddress: unwrappedSelectedTokenAddress,
    sourceChainId: depositViewChain,
    targetChainId: settlementChainId,
    initialTxGasLimit: quoteSendData?.gasLimit ?? baseQuoteSendData?.gasLimit,
    waitForTxGasLimit: true,
  });

  const gasPrice = useGasPrice(settlementChainId);

  const sameChainNetworkFeeAsyncResult = useThrottledAsync(
    async ({ params }) => {
      const client = getPublicClientWithRpc(params.settlementChainId);

      const token = getByKey(settlementChainTokensData, params.depositViewTokenAddress);
      if (token === undefined) {
        return undefined;
      }

      let inputAmount = params.inputAmount ?? 0n;
      if (inputAmount === 0n) {
        inputAmount = convertToTokenAmount(
          expandDecimals(10, USD_DECIMALS),
          token.decimals,
          getMidPrice(token.prices)
        )!;
      } else {
        inputAmount = bigMath.min(inputAmount, token.walletBalance ?? inputAmount);
      }

      return estimateSameChainDepositGas({
        chainId: params.settlementChainId as SettlementChainId,
        client,
        tokenAddress: params.depositViewTokenAddress,
        amount: inputAmount,
        account: params.account,
      });
    },
    {
      params:
        account && depositViewTokenAddress && settlementChainId
          ? {
              account,
              depositViewTokenAddress,
              inputAmount,
              settlementChainId,
            }
          : undefined,
      withLoading: false,
    }
  );
  const sameChainNetworkFeeDetails = useMemo(
    () =>
      calculateNetworkFeeDetails({
        gasLimit: sameChainNetworkFeeAsyncResult.data,
        gasPrice,
        tokensData: settlementChainTokensData,
      }),
    [sameChainNetworkFeeAsyncResult.data, gasPrice, settlementChainTokensData]
  );

  const isSameChainNetworkFeeLoading =
    sameChainNetworkFeeDetails === undefined && sameChainNetworkFeeAsyncResult.error === undefined;

  const paymentToken = useMemo((): TokenData | undefined => {
    if (selectedTokenData === undefined) {
      return undefined;
    }
    return {
      ...selectedTokenData,
      sourceChainBalance: selectedTokenBalanceForDeposit,
    };
  }, [selectedTokenBalanceForDeposit, selectedTokenData]);

  const gasPaymentToken = useMemo(() => {
    const nativeTokenData = getByKey(settlementChainTokensData, zeroAddress);
    if (nativeTokenData === undefined) {
      return undefined;
    }

    if (depositViewChain === settlementChainId) {
      return nativeTokenData;
    }

    const isSameNativeTokens = getMappedTokenId(
      settlementChainId as SettlementChainId,
      zeroAddress,
      depositViewChain as SourceChainId
    );
    if (!isSameNativeTokens) {
      return undefined;
    }

    return {
      ...nativeTokenData,
      sourceChainBalance: nativeTokenSourceChainBalance,
    };
  }, [depositViewChain, nativeTokenSourceChainBalance, settlementChainId, settlementChainTokensData]);

  const depositBalanceType =
    depositViewChain === settlementChainId ? TokenBalanceType.Wallet : TokenBalanceType.SourceChain;
  const ignoreGasPaymentToken =
    depositViewChain !== undefined &&
    depositViewChain !== settlementChainId &&
    !getMappedTokenId(settlementChainId as SettlementChainId, zeroAddress, depositViewChain as SourceChainId);
  const gasPaymentTokenBalanceForDeposit = getBalanceByBalanceType(gasPaymentToken, depositBalanceType);
  const gasPaymentTokenAmountForDepositView =
    depositViewChain === settlementChainId ? sameChainNetworkFeeDetails?.amount : networkFee;
  const needsGasPaymentTokenBuffer =
    !ignoreGasPaymentToken &&
    paymentToken !== undefined &&
    gasPaymentToken !== undefined &&
    paymentToken.address === gasPaymentToken.address;

  const isLoadingDepositMax =
    depositViewChain === settlementChainId
      ? false
      : isComposeGasLoading ||
        isBaseQuoteSendNativeFeeLoading ||
        ((inputAmount ?? 0n) > 0n && isQuoteSendNativeFeeLoading) ||
        (needsGasPaymentTokenBuffer && networkFee === undefined);

  const depositMaxDetails = useMaxAvailableAmount({
    fromToken: paymentToken,
    fromTokenBalance: getBalanceByBalanceType(paymentToken, depositBalanceType),
    fromTokenAmount: inputAmount,
    fromTokenInputValue: inputValue ?? "",
    isLoading: isLoadingDepositMax,
    srcChainId: depositViewChain,
    gasPaymentToken,
    gasPaymentTokenBalance: gasPaymentTokenBalanceForDeposit,
    gasPaymentTokenAmount: gasPaymentTokenAmountForDepositView,
    ignoreGasPaymentToken,
    useMinimalBuffer: depositViewChain !== undefined && depositViewChain !== settlementChainId,
  });

  const handleMaxButtonClick = useCallback(() => {
    if (depositMaxDetails.formattedMaxAvailableAmount) {
      setInputValue(depositMaxDetails.formattedMaxAvailableAmount);
    }
  }, [depositMaxDetails.formattedMaxAvailableAmount, setInputValue]);

  const isFirstDeposit = useIsFirstDeposit();
  const latestIsFirstDeposit = useLatest(isFirstDeposit);

  const subaccountState = useSubaccountContext();

  const isGeminiWallet = useIsGeminiWallet();
  const { isNonEoaAccountOnAnyChain } = useIsNonEoaAccountOnAnyChain();
  const isExpressTradingDisabled = isNonEoaAccountOnAnyChain || isGeminiWallet;
  const hasOutdatedUi = useHasOutdatedUi();
  const multipleWalletExtensionsChainError = useMultipleWalletExtensionsChainError();

  const sameChainCallback: TxnCallback<WalletTxnCtx> = useCallback(
    (txnEvent) => {
      if (!account) {
        return;
      }

      if (txnEvent.event === TxnEventName.Sent) {
        helperToast.success("Deposit sent", { toastId: "same-chain-gmx-account-deposit" });
        setIsVisibleOrView("transferHistory");
        setIsSubmitting(false);

        const sizeInUsdValue = latestInputAmountUsd.current;
        if (sizeInUsdValue !== undefined && sizeInUsdValue > 0n) {
          sendAddressablePixelEvent({
            eventName: AddressablePixelEventName.DepositSuccess,
            sizeInUsd: bigintToNumber(sizeInUsdValue, USD_DECIMALS),
            chainId: depositViewChain ?? settlementChainId,
          });
        }

        if (txnEvent.data.type === "wallet" && depositViewTokenAddress && inputAmount !== undefined) {
          const txnHash = txnEvent.data.transactionHash;
          const mockId = setMultichainSubmittedDeposit({
            amount: inputAmount,
            settlementChainId,
            sourceChainId: 0,
            tokenAddress: convertTokenAddress(settlementChainId, depositViewTokenAddress, "wrapped"),
            sentTxn: txnHash,
          });

          if (!mockId) {
            return;
          }

          if (!isHex(txnHash)) {
            return;
          }

          getPublicClientWithRpc(settlementChainId)
            .waitForTransactionReceipt({
              hash: txnHash,
            })
            .then((receipt) => {
              const bridgeInEvent = receipt.logs.find(
                (log) =>
                  isStringEqualInsensitive(log.address, getContract(settlementChainId, "EventEmitter")) &&
                  matchLogRequest(
                    encodeEventTopics({
                      abi: abis.EventEmitter,
                      eventName: "EventLog1",
                      args: { eventNameHash: "MultichainBridgeIn", topic1: toHex(addressToBytes32(account)) },
                    }),
                    log.topics
                  )
              );
              const bridgeInEventIndex = bridgeInEvent?.logIndex;
              if (bridgeInEventIndex === undefined) {
                return;
              }

              const id = `${txnHash.toLowerCase()}:${bridgeInEventIndex}`;
              setMultichainFundingPendingId(mockId, id);
            });
        }
      } else if (txnEvent.event === TxnEventName.Error) {
        helperToast.error(t`Deposit failed`, { toastId: "same-chain-gmx-account-deposit" });
        setIsSubmitting(false);
      }
    },
    [
      account,
      depositViewChain,
      depositViewTokenAddress,
      inputAmount,
      latestInputAmountUsd,
      setIsVisibleOrView,
      setMultichainFundingPendingId,
      setMultichainSubmittedDeposit,
      settlementChainId,
    ]
  );

  const handleSameChainDeposit = useCallback(async () => {
    if (!account || !depositViewTokenAddress || inputAmount === undefined) {
      return;
    }

    setIsSubmitting(true);

    await wrapChainAction(settlementChainId, setSettlementChainId, async (signer) => {
      await sendSameChainDepositTxn({
        chainId: settlementChainId as SettlementChainId,
        signer,
        tokenAddress: depositViewTokenAddress,
        amount: inputAmount,
        account,
        callback: sameChainCallback,
      });
    });
  }, [account, depositViewTokenAddress, inputAmount, sameChainCallback, settlementChainId, setSettlementChainId]);

  const makeCrossChainCallback = useCallback(
    (params: {
      depositViewChain: SourceChainId;
      metricId: OrderMetricId;
      sendParams: SendParam;
      tokenAddress: string;
    }): TxnCallback<WalletTxnCtx> =>
      (txnEvent) => {
        if (txnEvent.event === TxnEventName.Error) {
          setIsSubmitting(false);
          let prettyError = txnEvent.data.error;
          const data = txnEvent.data.error.info?.error?.data;

          if (isHex(data)) {
            const error = decodeErrorResult({
              abi: StargateErrorsAbi,
              data,
            });

            prettyError = new Error(JSON.stringify(error, null, 2));
            prettyError.name = error.errorName;

            const toastParams = getTxnErrorToast(
              params.depositViewChain,
              {
                errorMessage: JSON.stringify(error, null, 2),
              },
              { defaultMessage: t`Deposit failed` }
            );

            helperToast.error(toastParams.errorContent, {
              autoClose: toastParams.autoCloseToast,
              toastId: "gmx-account-deposit",
            });
          } else {
            const toastParams = getTxnErrorToast(params.depositViewChain, txnEvent.data.error, {
              defaultMessage: t`Deposit failed`,
            });

            helperToast.error(toastParams.errorContent, {
              autoClose: toastParams.autoCloseToast,
              toastId: "gmx-account-deposit",
            });
          }

          sendTxnErrorMetric(params.metricId, prettyError, "unknown");
        } else if (txnEvent.event === TxnEventName.Sent) {
          setIsSubmitting(false);

          sendTxnSentMetric(params.metricId);

          if (latestInputAmountUsd.current !== undefined) {
            sendAddressablePixelEvent({
              eventName: AddressablePixelEventName.DepositSuccess,
              sizeInUsd: bigintToNumber(latestInputAmountUsd.current, USD_DECIMALS),
              chainId: params.depositViewChain ?? settlementChainId,
            });
          }

          let submittedDepositGuid: string | undefined;

          if (txnEvent.data.type === "wallet") {
            const settlementChainDecimals = getToken(settlementChainId, params.tokenAddress)?.decimals;
            const sourceChainDecimals = getMappedTokenId(
              settlementChainId as SettlementChainId,
              params.tokenAddress,
              params.depositViewChain
            )?.decimals;

            if (settlementChainDecimals !== undefined && sourceChainDecimals !== undefined) {
              const amount = adjustForDecimals(
                params.sendParams.amountLD as bigint,
                sourceChainDecimals,
                settlementChainDecimals
              );

              submittedDepositGuid = setMultichainSubmittedDeposit({
                amount,
                settlementChainId,
                sourceChainId: params.depositViewChain,
                tokenAddress: params.tokenAddress,
                sentTxn: txnEvent.data.transactionHash,
              });
            }
          }

          if (submittedDepositGuid) {
            setSelectedTransferGuid(submittedDepositGuid);
            if (!subaccountState.subaccount && !isExpressTradingDisabled) {
              setIsVisibleOrView("depositStatus");
            } else {
              setIsVisibleOrView("transferHistory");
            }
          }
        } else if (txnEvent.event === TxnEventName.Simulated) {
          sendOrderSimulatedMetric(params.metricId);
        } else if (txnEvent.event === TxnEventName.Sending) {
          sendOrderTxnSubmittedMetric(params.metricId);
        }
      },
    [
      latestInputAmountUsd,
      settlementChainId,
      setMultichainSubmittedDeposit,
      setSelectedTransferGuid,
      subaccountState.subaccount,
      isExpressTradingDisabled,
      setIsVisibleOrView,
    ]
  );

  const canSendCrossChainDeposit =
    depositViewTokenAddress &&
    account &&
    amountLD !== undefined &&
    amountLD > 0n &&
    depositViewChain &&
    quoteSendNativeFee !== undefined &&
    sendParamsWithSlippage &&
    selectedTokenSourceChainTokenId;

  const handleCrossChainDeposit = useCallback(async (): Promise<boolean> => {
    if (!canSendCrossChainDeposit) {
      helperToast.error(t`Deposit failed`);
      return false;
    }

    setIsSubmitting(true);

    const metricData = initMultichainDepositMetricData({
      assetSymbol: selectedToken!.symbol,
      sizeInUsd: latestInputAmountUsd.current!,
      isFirstDeposit: latestIsFirstDeposit.current,
      settlementChain: settlementChainId,
      sourceChain: depositViewChain,
    });

    sendOrderSubmittedMetric(metricData.metricId);
    await wrapChainAction(depositViewChain, setSettlementChainId, async (signer) => {
      await sendCrossChainDepositTxn({
        chainId: depositViewChain,
        signer,
        tokenAddress: selectedTokenSourceChainTokenId.address,
        stargateAddress: selectedTokenSourceChainTokenId.stargate,
        amount: amountLD,
        quoteSendNativeFee,
        sendParams: sendParamsWithSlippage,
        account,
        callback: makeCrossChainCallback({
          depositViewChain,
          metricId: metricData.metricId,
          sendParams: sendParamsWithSlippage,
          tokenAddress: depositViewTokenAddress,
        }),
      });
    });

    return true;
  }, [
    account,
    amountLD,
    canSendCrossChainDeposit,
    depositViewChain,
    depositViewTokenAddress,
    latestInputAmountUsd,
    latestIsFirstDeposit,
    makeCrossChainCallback,
    quoteSendNativeFee,
    selectedToken,
    selectedTokenSourceChainTokenId?.address,
    selectedTokenSourceChainTokenId?.stargate,
    sendParamsWithSlippage,
    setSettlementChainId,
    settlementChainId,
  ]);

  const handleDeposit = useCallback(async () => {
    if (depositViewChain === settlementChainId) {
      await handleSameChainDeposit();
    } else {
      setIsSubmitting(true);
      setShouldSendCrossChainDepositWhenLoaded(true);
    }
  }, [depositViewChain, settlementChainId, handleSameChainDeposit]);

  const isCrossChainDepositLoading = useRef(false);
  useEffect(() => {
    if (!shouldSendCrossChainDepositWhenLoaded || isCrossChainDepositLoading.current) {
      return;
    }

    if (!canSendCrossChainDeposit) {
      return;
    }

    setShouldSendCrossChainDepositWhenLoaded(false);
    isCrossChainDepositLoading.current = true;
    handleCrossChainDeposit().finally(() => {
      isCrossChainDepositLoading.current = false;
    });
  }, [canSendCrossChainDeposit, handleCrossChainDeposit, shouldSendCrossChainDepositWhenLoaded]);

  useEffect(
    function fallbackDepositViewChain() {
      if (depositViewChain !== undefined || isVisibleOrView === false) {
        return;
      }

      setDepositViewChain(srcChainId ?? (settlementChainId as SourceChainId));
    },
    [depositViewChain, isVisibleOrView, setDepositViewChain, settlementChainId, srcChainId, walletChainId]
  );

  useEffect(
    function fallbackTokenOnSourceChain() {
      if (isVisibleOrView === false) {
        return;
      }

      const isInvalidTokenAddress =
        depositViewTokenAddress === undefined ||
        !MULTI_CHAIN_DEPOSIT_TRADE_TOKENS[settlementChainId as SettlementChainId].includes(
          depositViewTokenAddress as NativeTokenSupportedAddress
        );

      if (
        !isPriceDataLoading &&
        multichainTokens.length > 0 &&
        depositViewChain !== undefined &&
        isInvalidTokenAddress
      ) {
        const preferredToken = multichainTokens.find(
          (sourceChainToken) =>
            sourceChainToken.sourceChainId === depositViewChain &&
            sourceChainToken.address === CHAIN_ID_PREFERRED_DEPOSIT_TOKEN[settlementChainId as SettlementChainId]
        );

        if (
          preferredToken &&
          preferredToken.sourceChainBalance !== undefined &&
          preferredToken.sourceChainBalance >= 0n
        ) {
          setInputValue(undefined);
          setDepositViewTokenAddress(preferredToken.address);
          return;
        }

        let maxBalanceTokenAddress: string | undefined = undefined;
        let maxSourceChainBalanceUsd: bigint | undefined = undefined;

        for (const token of multichainTokens) {
          if (token.sourceChainId !== depositViewChain) {
            continue;
          }

          const balanceUsd = token.sourceChainPrices
            ? convertToUsd(token.sourceChainBalance, token.sourceChainDecimals, getMidPrice(token.sourceChainPrices))
            : 0n;
          if (
            maxBalanceTokenAddress === undefined ||
            maxSourceChainBalanceUsd === undefined ||
            (balanceUsd !== undefined && balanceUsd > maxSourceChainBalanceUsd)
          ) {
            maxBalanceTokenAddress = token.address;
            maxSourceChainBalanceUsd = balanceUsd;
          }
        }

        if (maxBalanceTokenAddress !== undefined) {
          setInputValue(undefined);
          setDepositViewTokenAddress(maxBalanceTokenAddress);
          return;
        }

        if (preferredToken) {
          setInputValue(undefined);
          setDepositViewTokenAddress(preferredToken.address);
        }
      }
    },
    [
      depositViewTokenAddress,
      isPriceDataLoading,
      multichainTokens,
      setDepositViewTokenAddress,
      settlementChainId,
      depositViewChain,
      isVisibleOrView,
      setInputValue,
    ]
  );

  useEffect(
    function fallbackTokenOnSettlementChain() {
      if (isVisibleOrView === false || depositViewChain !== settlementChainId) {
        return;
      }

      const depositTradeTokens = MULTI_CHAIN_DEPOSIT_TRADE_TOKENS[settlementChainId as SettlementChainId];

      const isInvalidTokenAddress =
        depositViewTokenAddress === undefined ||
        !depositTradeTokens.includes(depositViewTokenAddress as NativeTokenSupportedAddress) ||
        (getByKey(settlementChainTokensData, depositViewTokenAddress)?.walletBalance ?? 0n) === 0n;

      if (!isInvalidTokenAddress) {
        return;
      }

      const preferredTokenAddress = CHAIN_ID_PREFERRED_DEPOSIT_TOKEN[settlementChainId as SettlementChainId];
      const preferredTokenBalance = getByKey(settlementChainTokensData, preferredTokenAddress)?.walletBalance;

      if (preferredTokenBalance !== undefined && preferredTokenBalance > 0n) {
        setInputValue(undefined);
        setDepositViewTokenAddress(preferredTokenAddress);
        return;
      }

      let maxBalanceTokenAddress: string | undefined = undefined;
      let maxBalanceUsd: bigint | undefined = undefined;

      for (const tokenAddress of depositTradeTokens) {
        const tokenData = getByKey(settlementChainTokensData, tokenAddress);

        if (tokenData?.walletBalance === undefined || tokenData.walletBalance === 0n) {
          continue;
        }

        const balanceUsd = convertToUsd(tokenData.walletBalance, tokenData.decimals, getMidPrice(tokenData.prices));

        if (
          balanceUsd !== undefined &&
          (maxBalanceTokenAddress === undefined || maxBalanceUsd === undefined || balanceUsd > maxBalanceUsd)
        ) {
          maxBalanceTokenAddress = tokenAddress;
          maxBalanceUsd = balanceUsd;
        }
      }

      if (maxBalanceTokenAddress !== undefined) {
        setInputValue(undefined);
        setDepositViewTokenAddress(maxBalanceTokenAddress);
      }
    },
    [
      depositViewChain,
      depositViewTokenAddress,
      isVisibleOrView,
      setDepositViewTokenAddress,
      setInputValue,
      settlementChainId,
      settlementChainTokensData,
    ]
  );

  const isAvalancheSettlement = settlementChainId === AVALANCHE;

  const isGlobalEmpty = !isEligibilityLoading && !hasAnyDepositFunds;
  const isSelectedNetworkEmpty =
    !isEligibilityLoading &&
    !isGlobalEmpty &&
    depositViewChain !== undefined &&
    !hasDepositFundsOnChain(depositViewChain);
  const showEmptyState = (isGlobalEmpty || isSelectedNetworkEmpty) && !isAvalancheSettlement;

  const networkName = depositViewChain !== undefined ? getChainName(depositViewChain) : undefined;

  const depositTokenSymbols = useMemo(
    () =>
      depositViewChain !== undefined
        ? getDepositTokenSymbolsForNetwork(settlementChainId as SettlementChainId, depositViewChain)
        : EMPTY_ARRAY,
    [depositViewChain, settlementChainId]
  );
  const tokensList = useLocalizedList(depositTokenSymbols);
  const networksList = useLocalizedList(
    useMemo(() => depositNetworks.map((network) => network.name), [depositNetworks])
  );

  const handleDepositNetworkChange = useCallback(
    (networkId: number) => {
      if (networkId === depositViewChain) {
        return;
      }

      const newChain = networkId as SourceChainId;

      const isSelectedTokenAvailableOnNewChain =
        depositViewTokenAddress !== undefined &&
        (newChain === (settlementChainId as number)
          ? (getByKey(settlementChainTokensData, depositViewTokenAddress)?.walletBalance ?? 0n) > 0n
          : multichainTokens.some(
              (token) => token.address === depositViewTokenAddress && token.sourceChainId === newChain
            ));

      setDepositViewChain(newChain);
      setInputValue(undefined);

      if (!isSelectedTokenAvailableOnNewChain) {
        setDepositViewTokenAddress(undefined);
      }
    },
    [
      depositViewChain,
      depositViewTokenAddress,
      multichainTokens,
      setDepositViewChain,
      setDepositViewTokenAddress,
      setInputValue,
      settlementChainId,
      settlementChainTokensData,
    ]
  );

  const handleReceiveToWallet = useCallback(() => {
    setWalletReceiveViewChain(depositViewChain ?? (settlementChainId as SourceChainId));
    setWalletReceiveViewBackTo("deposit");
    setIsVisibleOrView("walletReceive");
  }, [depositViewChain, setIsVisibleOrView, setWalletReceiveViewBackTo, setWalletReceiveViewChain, settlementChainId]);

  const handleChangeNetworkClick = useCallback(() => {
    fromNetworkButtonRef.current?.click();
  }, []);

  const shouldShowInfoRowPlaceholder = inputAmount !== undefined && inputAmount > 0n;

  const areMultichainFeesLoading = isComposeGasLoading || isQuoteOftLoading || isQuoteSendNativeFeeLoading;

  const isNetworkFeeLoading =
    shouldShowInfoRowPlaceholder &&
    (depositViewChain === settlementChainId
      ? isSameChainNetworkFeeLoading
      : areMultichainFeesLoading || networkFee === undefined);

  const isInsufficientSourceChainNativeBalance =
    nativeTokenSourceChainBalance !== undefined &&
    networkFee !== undefined &&
    depositViewChain !== undefined &&
    networkFee + (unwrappedSelectedTokenAddress === zeroAddress ? amountLD ?? 0n : 0n) > nativeTokenSourceChainBalance;

  const isInsufficientSameChainNativeGasBalance =
    depositViewChain === settlementChainId &&
    gasPaymentTokenBalanceForDeposit !== undefined &&
    sameChainNetworkFeeDetails?.amount !== undefined &&
    sameChainNetworkFeeDetails.amount + (unwrappedSelectedTokenAddress === zeroAddress ? inputAmount ?? 0n : 0n) >
      gasPaymentTokenBalanceForDeposit;

  let buttonState: {
    text: React.ReactNode;
    bannerErrorName?: ValidationBannerErrorName;
    disabled?: boolean;
    onClick?: () => void;
    errorDescription?: ReactNode;
  } = {
    text: t`Deposit`,
    onClick: handleDeposit,
  };

  if (isAvalancheSettlement) {
    buttonState = {
      text: t`Not supported`,
      disabled: true,
    };
  } else if (hasOutdatedUi) {
    buttonState = {
      text: getPageOutdatedError(),
      disabled: true,
    };
  } else if (multipleWalletExtensionsChainError.buttonErrorMessage) {
    buttonState = {
      text: multipleWalletExtensionsChainError.buttonErrorMessage,
      errorDescription: multipleWalletExtensionsChainError.buttonTooltipMessage,
      disabled: true,
    };
  } else if (isApproving) {
    buttonState = {
      text: (
        <>
          <Trans>Approving...</Trans>
          <SpinnerIcon className="ml-4 animate-spin" />
        </>
      ),
      disabled: true,
    };
  } else if (needTokenApprove) {
    buttonState = {
      text: t`Allow ${selectedToken?.symbol} spending`,
      onClick: handleApproveClick,
    };
  } else if (isSubmitting) {
    buttonState = {
      text: (
        <>
          <Trans>Depositing...</Trans>
          <SpinnerIcon className="ml-4 animate-spin" />
        </>
      ),
      disabled: true,
    };
  } else if (isInputEmpty) {
    buttonState = {
      text: t`Enter deposit amount`,
      disabled: true,
    };
  } else if (selectedTokenBalanceForDeposit !== undefined && amountLD > selectedTokenBalanceForDeposit) {
    buttonState = {
      text: t`Insufficient balance`,
      disabled: true,
    };
  } else if (isInsufficientSourceChainNativeBalance) {
    buttonState = {
      text: getDefaultInsufficientGasMessage(),
      bannerErrorName: ValidationBannerErrorName.insufficientSourceChainNativeTokenBalance,
      disabled: true,
    };
  } else if (isInsufficientSameChainNativeGasBalance) {
    buttonState = {
      text: getDefaultInsufficientGasMessage(),
      disabled: true,
    };
  } else if (isNetworkFeeLoading) {
    buttonState = {
      text: (
        <>
          <Trans>Loading network fees...</Trans>
          <SpinnerIcon className="ml-4 animate-spin" />
        </>
      ),
      disabled: true,
    };
  }

  const onClick = buttonState.onClick;
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onClick?.();
    },
    [onClick]
  );

  const isTestnet = isTestnetChain(settlementChainId);

  const estimatedTimeValue = useMemo(() => {
    if (depositViewChain === settlementChainId) {
      return <Trans>Instant</Trans>;
    }

    if (inputAmount === undefined || inputAmount === 0n) {
      return "...";
    }

    return isTestnet ? <Trans>1m 40s</Trans> : <Trans>30s</Trans>;
  }, [depositViewChain, inputAmount, isTestnet, settlementChainId]);

  const depositFeeValue = useMemo(() => {
    if (depositViewChain === settlementChainId) {
      return <Trans>No fee</Trans>;
    }

    if (protocolFeeAmount === undefined || selectedTokenSourceChainDecimals === undefined) {
      return "...";
    }

    return (
      <AmountWithUsdBalance
        className="leading-1"
        amount={protocolFeeAmount}
        decimals={selectedTokenSourceChainDecimals}
        usd={protocolFeeUsd}
        symbol={selectedToken?.symbol}
      />
    );
  }, [
    depositViewChain,
    settlementChainId,
    protocolFeeAmount,
    selectedTokenSourceChainDecimals,
    protocolFeeUsd,
    selectedToken?.symbol,
  ]);

  const networkFeeValue = useMemo(() => {
    if (depositViewViemChain === undefined) {
      return "...";
    }

    if (depositViewChain === settlementChainId) {
      if (!sameChainNetworkFeeDetails) {
        return sameChainNetworkFeeAsyncResult.error !== undefined ? "-" : "...";
      }

      return (
        <AmountWithUsdBalance
          className="leading-1"
          amount={sameChainNetworkFeeDetails.amount}
          decimals={sameChainNetworkFeeDetails.decimals}
          usd={sameChainNetworkFeeDetails?.usd}
          symbol={sameChainNetworkFeeDetails?.symbol}
        />
      );
    }

    if (networkFee === undefined) {
      return "...";
    }

    return (
      <AmountWithUsdBalance
        className="leading-1"
        amount={networkFee}
        decimals={depositViewViemChain.nativeCurrency.decimals}
        usd={networkFeeUsd}
        symbol={depositViewViemChain.nativeCurrency.symbol}
      />
    );
  }, [
    networkFee,
    depositViewViemChain,
    depositViewChain,
    settlementChainId,
    networkFeeUsd,
    sameChainNetworkFeeDetails,
    sameChainNetworkFeeAsyncResult.error,
  ]);

  const isDepositFeeLoading = shouldShowInfoRowPlaceholder && areMultichainFeesLoading;

  const isGmxBalanceLoading = shouldShowInfoRowPlaceholder && isGmxAccountUsdLoading;

  return (
    <form className="flex grow flex-col overflow-y-auto px-adaptive pb-adaptive pt-adaptive" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-[--padding-adaptive]">
        {!showEmptyState && (
          <div className="flex flex-col gap-6">
            <div className="text-body-medium text-typography-secondary">
              <Trans>Asset</Trans>
            </div>
            <div
              tabIndex={0}
              role="button"
              onClick={() => {
                setIsVisibleOrView("selectAssetToDeposit");
              }}
              className="flex items-center justify-between rounded-8 border border-slate-800 bg-slate-800 px-14 py-13 gmx-hover:bg-fill-surfaceElevatedHover"
            >
              <div className="flex items-center gap-8">
                {selectedToken ? (
                  <>
                    <TokenIcon symbol={selectedToken.symbol} displaySize={20} />
                    <span className="text-16 leading-base">{selectedToken.symbol}</span>
                  </>
                ) : depositViewChain !== undefined ? (
                  <>
                    <Skeleton
                      baseColor="#B4BBFF1A"
                      highlightColor="#B4BBFF1A"
                      width={20}
                      height={20}
                      borderRadius={10}
                    />
                    <Skeleton baseColor="#B4BBFF1A" highlightColor="#B4BBFF1A" width={40} height={16} />
                  </>
                ) : (
                  <span className="text-typography-secondary">
                    <Trans>Select an asset to deposit</Trans>
                  </span>
                )}
              </div>
              <ChevronRightIcon className="size-14 text-typography-secondary" />
            </div>
          </div>
        )}
        <div className="flex flex-col gap-6">
          <div className="text-body-medium text-typography-secondary">
            <Trans>From network</Trans>
          </div>
          <DropdownSelector
            value={depositViewChain}
            onChange={handleDepositNetworkChange}
            placeholder={t`Select network`}
            buttonRef={fromNetworkButtonRef}
            button={
              depositViewChain !== undefined ? (
                <div className="flex items-center gap-8">
                  <img src={getChainIcon(depositViewChain)} alt={getChainName(depositViewChain)} className="size-20" />
                  <span className="text-16 leading-base">{getChainName(depositViewChain)}</span>
                </div>
              ) : undefined
            }
            options={depositNetworks}
            item={DepositNetworkItem}
            itemKey={depositNetworkItemKey}
          />
        </div>

        {!showEmptyState && (
          <div className={cx("flex flex-col gap-6", { invisible: depositViewTokenAddress === undefined })}>
            <div className="text-body-medium flex items-center justify-between gap-6 text-typography-secondary">
              <Trans>Deposit</Trans>
              {selectedToken !== undefined && (
                <div>
                  <Trans>Available</Trans>{" "}
                  <span className="text-typography-primary">
                    <span className="numbers">{depositMaxDetails.formattedBalance}</span> {selectedToken?.symbol}
                  </span>
                </div>
              )}
            </div>
            <div className="relative text-16 leading-base">
              <NumberInput
                value={inputValue}
                onValueChange={(e) => setInputValue(e.target.value)}
                className="w-full rounded-8 border border-slate-800 bg-slate-800 py-13 pl-12 pr-96 text-16 leading-base
                         focus-within:border-blue-300 hover:bg-fill-surfaceElevatedHover"
                placeholder="0.00"
                maxDecimals={selectedTokenSourceChainDecimals}
              />
              <div className="pointer-events-none absolute right-14 top-1/2 flex -translate-y-1/2 items-center gap-8">
                <span className="text-typography-secondary">{selectedToken?.symbol}</span>
                {depositMaxDetails.showClickMax && (
                  <button
                    className="text-body-small pointer-events-auto rounded-full bg-slate-600 px-8 py-2 font-medium
                             hover:bg-slate-500 focus-visible:bg-slate-500 active:bg-slate-500/70"
                    type="button"
                    onClick={handleMaxButtonClick}
                  >
                    <Trans>Max</Trans>
                  </button>
                )}
              </div>
            </div>
            {!selectedToken?.isStable && (
              <div className="text-body-medium text-typography-secondary numbers">
                {formatUsd(inputAmountUsd ?? 0n)}
              </div>
            )}
            {isAboveLimit && (
              <AlertInfoCard type="warning" className="mt-8" hideClose>
                <div>
                  <Trans>
                    GMX Account deposits are limited by Stargate bridge liquidity. Try an amount smaller than{" "}
                    <span className="numbers">{upperLimitFormatted}</span>.
                  </Trans>
                </div>
              </AlertInfoCard>
            )}
            {isBelowLimit && (
              <AlertInfoCard type="warning" className="mt-8" hideClose>
                <div>
                  <Trans>
                    Amount is below the deposit limit. Try an amount larger than{" "}
                    <span className="numbers">{lowerLimitFormatted}</span>.
                  </Trans>
                </div>
              </AlertInfoCard>
            )}
          </div>
        )}
      </div>

      {showEmptyState ? (
        <div className="flex shrink-0 grow flex-col items-center justify-center gap-8 py-32 text-center">
          <div className="text-body-large font-medium">
            {isGlobalEmpty ? (
              <Trans>No funds available for GMX Account deposit</Trans>
            ) : (
              <Trans>No funds available to deposit from {networkName}</Trans>
            )}
          </div>
          <div className="text-body-medium text-typography-secondary">
            {isGlobalEmpty ? (
              <Trans>
                Receive supported assets into your wallet on {networksList}, then deposit them to GMX Account.
              </Trans>
            ) : (
              <Trans>
                Receive {tokensList} into your wallet on {networkName}, or choose another network.
              </Trans>
            )}
          </div>
        </div>
      ) : (
        <div className="h-32 shrink-0 grow" />
      )}

      {!showEmptyState && depositViewTokenAddress && (
        <div className="mb-16 flex flex-col gap-10">
          <SyntheticsInfoRow label={<Trans>Estimated time</Trans>} value={estimatedTimeValue} />
          <SyntheticsInfoRow
            label={<Trans>Network fee</Trans>}
            value={isNetworkFeeLoading ? valueSkeleton : networkFeeValue}
          />
          <SyntheticsInfoRow
            label={<Trans>Deposit fee</Trans>}
            value={isDepositFeeLoading ? valueSkeleton : depositFeeValue}
          />
          <SyntheticsInfoRow
            label={<Trans>GMX balance</Trans>}
            value={
              isGmxBalanceLoading ? (
                valueSkeleton
              ) : (
                <ValueTransition from={formatUsd(gmxAccountUsd)} to={formatUsd(nextGmxAccountBalanceUsd)} />
              )
            }
          />
        </div>
      )}

      <div className="sticky bottom-0 z-10 flex shrink-0 flex-col gap-10 bg-slate-900 pt-8">
        {showEmptyState ? (
          <>
            <Button variant="primary-action" className="w-full shrink-0" type="button" onClick={handleReceiveToWallet}>
              <Trans>Receive to Wallet</Trans>
            </Button>
            <Button
              variant="secondary"
              size="medium"
              className="w-full shrink-0"
              type="button"
              onClick={handleChangeNetworkClick}
            >
              <Trans>Change network</Trans>
            </Button>
          </>
        ) : (
          <>
            {buttonState.bannerErrorName && (
              <AlertInfoCard type="error" hideClose>
                <ValidationBannerErrorContent
                  validationBannerErrorName={buttonState.bannerErrorName}
                  chainId={settlementChainId}
                  srcChainId={depositViewChain as SourceChainId | undefined}
                />
              </AlertInfoCard>
            )}

            <ButtonTooltipWrapper content={buttonState.errorDescription}>
              <Button
                variant="primary-action"
                className="w-full shrink-0"
                type="submit"
                disabled={buttonState.disabled}
              >
                {buttonState.text}
              </Button>
            </ButtonTooltipWrapper>
          </>
        )}
      </div>
    </form>
  );
};

function depositNetworkItemKey(option: { id: number; name: string }) {
  return option.id;
}

function DepositNetworkItem({ option }: { option: { id: number; name: string } }) {
  return (
    <div className="flex items-center gap-8">
      <img src={getChainIcon(option.id)} alt={option.name} className="size-20" />
      <span className="text-body-large">{option.name}</span>
    </div>
  );
}
